/**
 * #431 (bucket 1) — client-side auth-invalidation store.
 *
 * The SPA fetches `/api/session` once on mount and caches it; nothing re-checks
 * it, so a server-side logout (e.g. in ANOTHER tab — the session cookie is
 * destroyed) is invisible until a hard refresh. Two things fix that:
 *
 *  1. App.tsx re-fetches the session on window `focus` / `visibilitychange`.
 *  2. This store: a single openapi-fetch middleware (wired in `api/client.ts`)
 *     flips it the instant ANY authenticated `/api` call returns `401`, so the
 *     app can drop to the sign-in affordance without waiting for a refocus.
 *
 * The server remains the sole auth authority; this store only mirrors a 401 so
 * the UI reconciles promptly. It can never *grant* access.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

/** Notify subscribers that an authenticated call was refused (401). */
export function noteUnauthenticated(): void {
  for (const l of listeners) l();
}

/** Subscribe to 401 signals; returns an unsubscribe fn. */
export function subscribeUnauthenticated(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Inspect a fetched Response; on a 401 signal the store. A no-op otherwise.
 * The `/session` bootstrap 401 is handled by `fetchSession` itself (embedded →
 * NotAuthenticatedError; standalone → redirect), so this catches 401s from any
 * OTHER authenticated call made after sign-in (mid-session logout elsewhere).
 */
export function noteUnauthenticatedFrom(response: Response): void {
  if (response.status === 401) noteUnauthenticated();
}
